import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';

import AddMilk from './AddMilk';
import {
  isEnableLoadingSelector,
  milkListSelector,
} from './selector';
import { deleteMilk, getMilkList, refreshMilkList } from './thunk';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { MilkJson } from '@/types/domain/milk/entities/milkEntity';
import type { UserType } from '@/types/domain/user/redux';

import AdditionalLoading from '@/components/AdditionalLoading';
import Delete from '@/components/Delete';
import TableBase from '@/components/TableBase';
import MilkInfo from '@/domain/milk/component/MilkInfo';
import { MeasureDateTime } from '@/domain/temperature/service/entity/valueObjects/measureDateTime';
import { toStringDateTimeDisplay } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
};

const Milk: React.FC<Props> = ({ user, child, facilityList }) => {
  const dispatch = useDispatch();
  const isEnableLoading = useSelector(isEnableLoadingSelector);
  const [
    isOpen,
    setIsOpen,
  ] = React.useState<boolean>(false);
  const milkList = useSelector(milkListSelector);
  const [
    deleteMilkJson,
    setDeleteMilkJson,
  ] = React.useState<MilkJson | undefined>(undefined);
  const rows: TableBaseRowType[] = [];
  for (let i = 0; i < milkList.length; i += 1) {
    if (milkList[i].data.childId === child.childId) {
      rows.push({
        id: i.toString(),
        measureDateTime: toStringDateTimeDisplay(new Date(milkList[i].data.measureDateTime), false),
        milk: milkList[i].data.milk.toString(),
        type: milkList[i].data.measurerType,
      });
    }
  }
  const columns: TableBaseColumnType[] = [
    { id: 'measureDateTime', label: MeasureDateTime.label, minWidth: 110 },
    { id: 'milk', label: '量(ml)', minWidth: 30 },
  ];
  const actions = [];
  actions.push({
    icon: <DeleteIcon />,
    title: '削除',
    action: (r: TableBaseRowType) => {
      const item = milkList[parseInt(r.id, 10)];
      setDeleteMilkJson(item);
    },
    disabled: (r: TableBaseRowType) => r.type !== 'app',
  });
  return (
    <div>
      <div>
        <TableBase
          rows={rows}
          columns={columns}
          actions={actions}
          addAction={() => setIsOpen(true)}
          refreshAction={() => dispatch(refreshMilkList())}
          isPagination={false}
          isShowFilter={false}
          isShowFooter={false}
        />
        <AdditionalLoading
          isEnableLoading={isEnableLoading}
          onClick={() => dispatch(getMilkList())}
        />
        <AddMilk
          isOpen={isOpen}
          close={() => setIsOpen(false)}
          user={user}
          child={child}
          facilityList={facilityList}
        />
        <Delete<MilkJson>
          comp={MilkInfo}
          data={deleteMilkJson}
          onDelete={(json) => {
            if (json) {
              dispatch(deleteMilk({
                data: json,
                facilityIds: facilityList.map((v) => v.data.facilityId),
              }));
            }
            setDeleteMilkJson(undefined);
          }}
          title="ミルクの記録"
          content="以下の記録を削除しますか？"
        />
      </div>
    </div>
  );
};

export default Milk;
